/**
 * Purchase — document model and configuration.
 *
 * Mirrors the sales chain (Purchase Order → Goods Receipt → Bill → Payment,
 * with Debit Notes against posted bills) but the accounting is not a mirror
 * image. Sales output tax is a liability you owe; purchase input tax is a
 * credit you may only claim once several conditions are met. That asymmetry
 * lives in itc.ts and is the reason this is a separate module rather than a
 * flag on the sales one.
 */

import type { SalesCharge, SalesLine, SupplyType } from "../sales/types";

export type PurchaseDocType =
  | "purchase-request"
  | "purchase-order"
  | "goods-receipt"
  | "bill"
  | "debit-note"
  | "credit-note-received"
  | "payment";

/** Purchase lines reuse the sales line shape — the tax maths is identical. */
export type PurchaseLine = SalesLine & {
  /** Nature of the expense, which drives ITC eligibility and TDS section. */
  expenseNature?: ExpenseNature;
  /** Set when the buyer disputes the billed quantity against the receipt. */
  acceptedQuantity?: number;
  rejectedQuantity?: number;
};

/**
 * Expense nature. This is the single most useful field on a purchase line: it
 * decides whether input credit is available at all, which TDS section applies,
 * and whether reverse charge is triggered.
 */
export type ExpenseNature =
  | "goods-for-resale"
  | "raw-material"
  | "capital-goods"
  | "works-contract"
  | "professional-services"
  | "contractor-services"
  | "rent"
  | "commission"
  | "transport-gta"
  | "legal-services"
  | "director-services"
  | "sponsorship"
  | "import-of-services"
  | "employee-welfare"
  | "food-and-beverage"
  | "motor-vehicle"
  | "insurance-life-health"
  | "club-membership"
  | "construction-immovable"
  | "csr"
  | "general-expense";

export type PurchaseDocStatus =
  | "Draft"
  | "Pending review"
  | "Approved"
  | "Sent"
  | "Confirmed"
  | "Partially received"
  | "Received"
  | "Posted"
  | "Partially paid"
  | "Paid"
  | "On hold"
  | "Closed"
  | "Cancelled"
  | "Rejected";

export interface PurchaseDocument {
 canonical?:boolean;adjustments?:import('../document-adjustments').DocumentAdjustments;
  id: string;
  type: PurchaseDocType;
  number: string;
  /** The vendor's own document number — a bill is filed under theirs, not ours. */
  vendorDocumentNumber?: string;
  vendorDocumentDate?: string;
  date: string;
  dueDate?: string;
  partnerId: string;
  partnerName: string;
  partnerGstin?: string;
  placeOfSupply: string;
  supplyType: SupplyType;
  status: PurchaseDocStatus;
  currency: string;
  exchangeRate?: number;
  lines: PurchaseLine[];
  charges?: SalesCharge[];
  documentDiscount?: number;
  notes?: string;
  /** Chain links. */
  purchaseRequestId?: string;
  purchaseOrderId?: string;
  goodsReceiptIds?: string[];
  billId?: string;
  againstBillId?: string;
  /** Reverse charge — we pay the tax instead of the vendor. */
  rcm?: boolean;
  /** TDS we must deduct when paying this vendor. */
  tdsSection?: string;
  tdsRate?: number;
  tdsAmount?: number;
  /** ITC decision, set when the bill is reviewed. */
  itcEligibility?: ItcEligibility;
  /** Appears in GSTR-2B — a precondition for claiming credit. */
  reflectedIn2B?: boolean;
  twoBPeriod?: string;
  journalId?: string;
  postedAt?: string;
  maker?: string;
  reviewer?: string;
  createdAt: string;
  updatedAt?: string;
  balance?: number;
  /** Payment held pending a query. */
  holdReason?: string;
}

export type ItcEligibility = "eligible" | "blocked-17-5" | "ineligible-personal" | "pending-2b" | "reversed-180-day" | "not-applicable";

export const ITC_LABELS: Record<ItcEligibility, string> = {
  eligible: "Eligible",
  "blocked-17-5": "Blocked under section 17(5)",
  "ineligible-personal": "Ineligible — non-business use",
  "pending-2b": "Pending — not in GSTR-2B",
  "reversed-180-day": "Reversed — unpaid beyond 180 days",
  "not-applicable": "Not applicable"
};

// -------------------------------------------------------------- Payments

export type PaymentMode = "cash" | "bank-transfer" | "cheque" | "upi" | "card" | "adjustment";

export interface PaymentAllocation {
  billId: string;
  billNumber: string;
  amount: number;
}

export interface Payment {
  id: string;
  number: string;
  date: string;
  partnerId: string;
  partnerName: string;
  /** Cash actually paid, after TDS. */
  amount: number;
  currency: string;
  mode: PaymentMode;
  reference?: string;
  bankAccountCode?: string;
  allocations: PaymentAllocation[];
  unallocated: number;
  /** TDS deducted while paying — a liability until deposited. */
  tdsDeducted?: number;
  tdsSection?: string;
  status: "Draft" | "Posted" | "Cancelled";
  journalId?: string;
  notes?: string;
  createdAt: string;
}

// ---------------------------------------------------------------- Config

export interface PurchaseConfig {
  /** A bill must originate from an approved purchase order. */
  requirePurchaseOrder: boolean;
  /** Goods must be received before the bill can post. */
  requireGoodsReceipt: boolean;
  /** Bill quantity and rate must agree with the order and the receipt. */
  enforceThreeWayMatch: boolean;
  /** Tolerance on quantity and price before the match fails. */
  quantityTolerancePercent: number;
  priceTolerancePercent: number;
  allowPartialBilling: boolean;
  requireBillApproval: boolean;
  roundOff: boolean;
  roundOffAccountCode: string;
  defaultCreditDays: number;
  /** Block payment to a vendor with no PAN, since TDS runs at 20%. */
  blockPaymentWithoutPan: boolean;
  /** Warn when an MSME bill approaches its section 15 limit. */
  msmeAlertDays: number;
  /** Do not allow a bill to post while it is missing from GSTR-2B. */
  blockItcWithout2B: boolean;
  supplierStateCode: string;
  numbering: Record<string, { pattern: string; prefix: string; nextNumber: number; padding: number; resetAnnually: boolean }>;
}

export const defaultPurchaseConfig = (supplierStateCode = "29"): PurchaseConfig => ({
  requirePurchaseOrder: false,
  requireGoodsReceipt: false,
  enforceThreeWayMatch: false,
  quantityTolerancePercent: 0,
  priceTolerancePercent: 0,
  allowPartialBilling: true,
  requireBillApproval: true,
  roundOff: true,
  roundOffAccountCode: "1920",
  defaultCreditDays: 30,
  blockPaymentWithoutPan: false,
  msmeAlertDays: 7,
  blockItcWithout2B: false,
  supplierStateCode,
  numbering: {
    "purchase-request": { pattern: "{PREFIX}/{FY}/{####}", prefix: "PR", nextNumber: 1, padding: 4, resetAnnually: true },
    "purchase-order": { pattern: "{PREFIX}/{FY}/{####}", prefix: "PO", nextNumber: 1, padding: 4, resetAnnually: true },
    "goods-receipt": { pattern: "{PREFIX}/{FY}/{####}", prefix: "GRN", nextNumber: 1, padding: 4, resetAnnually: true },
    bill: { pattern: "{PREFIX}/{FY}/{####}", prefix: "BILL", nextNumber: 1, padding: 4, resetAnnually: true },
    "debit-note": { pattern: "{PREFIX}/{FY}/{####}", prefix: "DBN", nextNumber: 1, padding: 4, resetAnnually: true },
    "credit-note-received": { pattern: "{PREFIX}/{FY}/{####}", prefix: "CNR", nextNumber: 1, padding: 4, resetAnnually: true },
    payment: { pattern: "{PREFIX}/{FY}/{####}", prefix: "PMT", nextNumber: 1, padding: 4, resetAnnually: true }
  }
});

// ----------------------------------------------------------------- Audit

export interface PurchaseAuditEvent {
  id: string;
  documentId: string;
  documentType: PurchaseDocType;
  documentNumber: string;
  action: string;
  fromStatus?: PurchaseDocStatus;
  toStatus?: PurchaseDocStatus;
  user: string;
  at: string;
  changes?: Array<{ field: string; from: unknown; to: unknown }>;
  note?: string;
}

// -------------------------------------------------------- State machines

export const TRANSITIONS: Record<PurchaseDocType, Partial<Record<PurchaseDocStatus, PurchaseDocStatus[]>>> = {
  "purchase-request": {
    Draft: ["Pending review", "Cancelled"],
    "Pending review": ["Approved", "Rejected", "Draft"],
    Approved: ["Closed", "Cancelled"],
    Rejected: [],
    Closed: [],
    Cancelled: []
  },
  "purchase-order": {
    Draft: ["Pending review", "Confirmed", "Cancelled"],
    "Pending review": ["Approved", "Draft", "Cancelled"],
    Approved: ["Sent", "Confirmed", "Cancelled"],
    Sent: ["Confirmed", "Cancelled"],
    Confirmed: ["Partially received", "Received", "Closed", "Cancelled"],
    "Partially received": ["Received", "Closed", "Cancelled"],
    Received: ["Closed"],
    Closed: [],
    Cancelled: []
  },
  "goods-receipt": {
    Draft: ["Received", "Cancelled"],
    Received: ["Closed", "Cancelled"],
    Closed: [],
    Cancelled: []
  },
  bill: {
    Draft: ["Pending review", "Posted", "On hold", "Cancelled"],
    "Pending review": ["Approved", "Draft", "On hold", "Cancelled"],
    Approved: ["Posted", "Draft", "On hold", "Cancelled"],
    "On hold": ["Pending review", "Approved", "Cancelled"],
    // A posted bill is corrected by debit note, never edited.
    Posted: ["Partially paid", "Paid", "Cancelled"],
    "Partially paid": ["Paid", "Cancelled"],
    Paid: [],
    Cancelled: []
  },
  "debit-note": {
    Draft: ["Pending review", "Posted", "Cancelled"],
    "Pending review": ["Approved", "Draft", "Cancelled"],
    Approved: ["Posted", "Cancelled"],
    Posted: [],
    Cancelled: []
  },
  "credit-note-received": {
    Draft: ["Pending review", "Posted", "Cancelled"],
    "Pending review": ["Approved", "Draft", "Cancelled"],
    Approved: ["Posted", "Cancelled"],
    Posted: [],
    Cancelled: []
  },
  payment: { Draft: ["Pending review", "Posted", "Cancelled"], "Pending review": ["Approved", "Draft", "Cancelled"], Approved: ["Posted", "Cancelled"], Posted: ["Cancelled"], Cancelled: [] }
};

export const LOCKED_STATUSES: PurchaseDocStatus[] = ["Posted", "Partially paid", "Paid", "Cancelled", "Closed", "Rejected"];

export const isLocked = (document: Pick<PurchaseDocument, "status">): boolean =>
  LOCKED_STATUSES.includes(document.status);

export const canTransition = (type: PurchaseDocType, from: PurchaseDocStatus, to: PurchaseDocStatus): boolean =>
  (TRANSITIONS[type]?.[from] ?? []).includes(to);

export const DOC_LABELS: Record<PurchaseDocType, string> = {
  "purchase-request": "Purchase request",
  "purchase-order": "Purchase order",
  "goods-receipt": "Goods receipt note",
  bill: "Vendor bill",
  "debit-note": "Debit note",
  "credit-note-received": "Credit note received",
  payment: "Payment"
};

